import { IBuildInfo } from "cordova-plugin-buildinfo"
import { deviceDetect } from "react-device-detect"
import { createEnumChecker } from "shared/Helpers/enums"
import { EnvironmentType, EnvironmentMap, Environment, CurrentEnvironment } from "./envTypes"

const isEnvironmentType = createEnumChecker(EnvironmentType)

const localhost = "http://localhost:3000"
const getHost = (env: Exclude<EnvironmentType, "local">) => `https://${env}.facilitynet.dk`

const getWebappUrl = (env: EnvironmentType) => (env === EnvironmentType.Local) ?
        localhost :
        `${getHost(env)}/mobile-app`

const getDesktopUrl = (env: EnvironmentType) => (env === EnvironmentType.Prod || env === EnvironmentType.QA) ?
        "https://facilitynet.dk/gopay" : "https://facilitynet.dk/gopay-demo"

const getApiUrl = (env: EnvironmentType) => (env === EnvironmentType.Local) ?
        `${localhost}/api` :
        `${getHost(env)}/api`

const environmentList: Environment[] = Object.values(EnvironmentType)
    .map(e => ({
        type: e,
        webapp: {
            domainUrl: e === EnvironmentType.Local ? localhost : getHost(e),
            baseUrl: getWebappUrl(e),
            desktopUrl: getDesktopUrl(e)
        },
        api: {
            baseUrl: getApiUrl(e)
        }
    }))

const environments = environmentList.reduce((m, e) => ({
    [e.type]: e,
    ...m
}), {} as EnvironmentMap)

export function getCurrentEnvironmentType() {
    const envStr = import.meta.env.VITE_TARGET_ENV
    if (envStr && isEnvironmentType(envStr)) {
        return envStr
    }
    return EnvironmentType.Local
}

export const getAppVersion = (buildInfo: IBuildInfo) => buildInfo.version

export function createEnvironment(buildInfo: IBuildInfo | undefined) {
    const deviceInfo = deviceDetect(undefined)

    const currentEnvType = getCurrentEnvironmentType()
    const app = buildInfo ? ({ app: { version: getAppVersion(buildInfo) }}) : undefined
    const deployPath = import.meta.env.VITE_DEPLOY_PATH
    const deployTime = import.meta.env.VITE_DEPLOY_TIME
    const deployInfo = deployTime ? { deployPath, deployTime } : undefined

    const currentEnv: CurrentEnvironment = {
        type: currentEnvType,
        ...app,
        webapp: {
            webappOnlyUrl: import.meta.env.VITE_WEBAPP_URL,
            baseUrl: import.meta.env.VITE_APP_URL ?? env(currentEnvType).webapp.baseUrl,
            desktopUrl: env(currentEnvType).webapp.desktopUrl,
            version: import.meta.env.VITE_WEBAPP_VERSION ?? "LIVE",
            versionFull: import.meta.env.VITE_WEBAPP_VERSION_FULL ?? "LIVE",
            ...deployInfo,
        },
        api: {
            baseUrl: import.meta.env.VITE_API_ENDPOINT ?? env(currentEnvType).api.baseUrl,
            gateUrl: import.meta.env.VITE_GATE_ENDPOINT ?? `${localhost}/gate`
        },
        device: deviceInfo
    }

    return currentEnv
}

export const env = (type: EnvironmentType) => environments[type]
